import styled from "styled-components";
import { useEffect, useRef, useState } from "react";

const LineWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 10px;
`;

const CheckedLine = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 5px;

  background-color: white;
  background-image:
    linear-gradient(45deg, #ccc 25%, transparent 25%),
    linear-gradient(-45deg, #ccc 25%, transparent 25%),
    linear-gradient(45deg, transparent 75%, #ccc 75%),
    linear-gradient(-45deg, transparent 75%, #ccc 75%);

  background-size: 40px 40px;
  background-position:
    0 0,
    0 20px,
    20px -20px,
    -20px 0px;
`;

const FaceLine = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 5px;

  background: linear-gradient(to right, transparent, var(--bs-primary));
`;

const CursorLine = styled.div`
  position: absolute;
  top: -4px; /* slightly taller than bar */
  bottom: -4px;
  width: 5px;
  background: black;
  border-radius: 3px;
  transform: translateX(-50%);
  outline: 1px solid white; /* makes it visible on top of the checked pattern */
`;

const clamp = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

interface OpacitySliderProps {
  onChange?: (value: number) => void;
  value: number;
}

const OpacitySlider = ({ onChange, value: propValue }: OpacitySliderProps) => {
  const barRef = useRef<HTMLDivElement | null>(null);
  const [value, setValue] = useState(
    propValue !== undefined ? Math.round(propValue * 100) : 100,
  );
  const [dragging, setDragging] = useState(false);

  const updateFromEvent = (e: MouseEvent) => {
    const rect = barRef.current!.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const pct = (x / rect.width) * 100;
    setValue(clamp(pct, 0, 100));
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setDragging(true);
    updateFromEvent(e as any); // jump to where you first click
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!dragging) return;
      updateFromEvent(e);
    };

    const handleUp = () => setDragging(false);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
  }, [dragging]);

  useEffect(() => {
    onChange?.(Number((value / 100).toFixed(2)));
  }, [value, onChange]);

  return (
    <LineWrapper ref={barRef} onMouseDown={handleMouseDown}>
      <CheckedLine />
      <FaceLine />
      <CursorLine style={{ left: `${value}%` }} />
    </LineWrapper>
  );
};

export default OpacitySlider;
